import type { LevelDefinition } from "@algoquest/schemas";
import { createCampaignLevel } from "./levelFactory";

/**
 * Phase 15 -- World 3: "Binary Search" (Master Spec Section 5), campaign
 * levels 21-30. Teaches: sorted-data prerequisite, search range, middle element, comparison, discard half, repeat, reduction of search space.
 * Algorithm(s) used in this world: binary-search.
 */
export const world03BinarySearchLevels: LevelDefinition[] = [
  createCampaignLevel({
    id: "campaign-021",
    world: 3,
    levelInWorld: 1,
    globalLevelNumber: 21,
    algorithm: "binary-search",
    values: [3, 8, 14, 21, 29, 38, 50],
    target: 21,
    targetPresent: true,
    isSorted: true,
    learningObjectives: [
      "Learn that Binary Search requires the dataset to already be sorted.",
      "See the very first inspection land exactly at the middle of the range, not the start.",
    ],
    notes: "7 sorted values -- the middle index holds the target, so Binary Search finds it in a single comparison.",
  }),
  createCampaignLevel({
    id: "campaign-022",
    world: 3,
    levelInWorld: 2,
    globalLevelNumber: 22,
    algorithm: "binary-search",
    values: [3, 8, 14, 21, 29, 38, 50],
    target: 8,
    targetPresent: true,
    isSorted: true,
    learningObjectives: [
      "Practice choosing LEFT when the target is smaller than the middle value.",
      "See the right half discarded and never inspected again.",
    ],
    notes: "mid=21 -> target smaller -> discard right half -> mid=8 -> FOUND in two steps.",
  }),
  createCampaignLevel({
    id: "campaign-023",
    world: 3,
    levelInWorld: 3,
    globalLevelNumber: 23,
    algorithm: "binary-search",
    values: [3, 8, 14, 21, 29, 38, 50],
    target: 38,
    targetPresent: true,
    isSorted: true,
    learningObjectives: [
      "Practice choosing RIGHT when the target is larger than the middle value.",
      "See the left half discarded, including the value already inspected at level 22.",
    ],
    notes: "mid=21 -> target larger -> discard left half -> mid=38 -> FOUND in two steps.",
  }),
  createCampaignLevel({
    id: "campaign-024",
    world: 3,
    levelInWorld: 4,
    globalLevelNumber: 24,
    algorithm: "binary-search",
    values: [3, 8, 14, 21, 29, 38, 50],
    target: 25,
    targetPresent: false,
    isSorted: true,
    learningObjectives: [
      "See the NOT_FOUND outcome once the search range shrinks past empty, even though every choice was correct.",
      "Understand a wrong-looking outcome can still mean every decision was right -- the value is simply absent.",
    ],
    notes: "25 sits between 21 and 29 in sorted order but is not itself in the dataset -- the range narrows to nothing.",
  }),
  createCampaignLevel({
    id: "campaign-025",
    world: 3,
    levelInWorld: 5,
    globalLevelNumber: 25,
    algorithm: "binary-search",
    values: [4, 9, 15, 23, 31, 40, 48, 55, 63],
    target: 63,
    targetPresent: true,
    isSorted: true,
    learningObjectives: [
      "Watch the search range (low/high) shrink toward one end across repeated steps.",
      "See that going the same direction more than once is normal when the target is near an edge.",
    ],
    notes: "target 63 is the last of 9 sorted values -- repeated RIGHT choices demonstrate the range narrowing step by step.",
  }),
  createCampaignLevel({
    id: "campaign-026",
    world: 3,
    levelInWorld: 6,
    globalLevelNumber: 26,
    algorithm: "binary-search",
    values: [4, 9, 15, 23, 31, 40, 48, 55, 63],
    target: 4,
    targetPresent: true,
    isSorted: true,
    learningObjectives: [
      "See repeated LEFT choices narrow the range toward the very first value.",
      "Contrast this with level 25's repeated RIGHT choices on the same dataset.",
    ],
    notes: "target 4 is the first of 9 sorted values -- mirrors level 25's path in the opposite direction.",
  }),
  createCampaignLevel({
    id: "campaign-027",
    world: 3,
    levelInWorld: 7,
    globalLevelNumber: 27,
    algorithm: "binary-search",
    values: [4, 9, 15, 23, 31, 40, 48, 55, 63],
    target: 50,
    targetPresent: false,
    isSorted: true,
    learningObjectives: [
      "Complete a longer not found search and see how few comparisons it still takes.",
      "Build intuition for why halving the range matters as datasets grow.",
    ],
    notes: "50 is absent from these 9 sorted values -- the range still narrows to nothing in only a handful of steps.",
  }),
  createCampaignLevel({
    id: "campaign-028",
    world: 3,
    levelInWorld: 8,
    globalLevelNumber: 28,
    algorithm: "binary-search",
    values: [2, 6, 11, 17, 23, 30, 38, 47, 57, 68, 80],
    target: 17,
    targetPresent: true,
    isSorted: true,
    learningObjectives: [
      "Practice the full inspect-then-choose loop across three consecutive steps.",
      "See the search range shrink from 11 candidates down to one in just a few comparisons.",
    ],
    notes: "11 sorted values, target found after three narrowing steps -- each step eliminates roughly half of what remained.",
  }),
  createCampaignLevel({
    id: "campaign-029",
    world: 3,
    levelInWorld: 9,
    globalLevelNumber: 29,
    algorithm: "binary-search",
    values: [1, 5, 10, 16, 22, 29, 37, 46, 56, 67, 79, 93],
    target: 93,
    targetPresent: true,
    isSorted: true,
    learningObjectives: [
      "See Binary Search eliminate a large range quickly even when the target is at an extreme end.",
      "Compare how few comparisons this needs versus what Linear Search would need on the same 12 values.",
    ],
    notes: "target 93 is the last of 12 sorted values -- repeated RIGHT choices still resolve it in only a few steps.",
  }),
  createCampaignLevel({
    id: "campaign-030",
    world: 3,
    levelInWorld: 10,
    globalLevelNumber: 30,
    algorithm: "binary-search",
    values: [2, 5, 9, 14, 20, 27, 35, 44, 54, 65, 77, 90, 104, 119, 135],
    target: 100,
    targetPresent: false,
    isSorted: true,
    learningObjectives: [
      "Complete the world's largest not found search and see how few steps it still requires.",
      "Consolidate Binary Search: sorted prerequisite, middle inspection, discarding a half, and repeating until the range is empty.",
    ],
    notes: "World 3 capstone -- 15 sorted values, target absent. Even at this size, the search space shrinks to nothing in only a handful of comparisons.",
  }),
];
